import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,  // ← AJOUTÉ !
  Divider,
  Badge,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
  Menu,
  MenuItem as MuiMenuItem
} from '@mui/material';
import {
  Send,
  Search,
  AttachFile,
  Image,
  InsertEmoticon,
  MoreVert,
  Delete,
  PersonAdd,
  GroupAdd,
  Close,
  CheckCircle,
  DoneAll,
  Schedule,
  Phone,
  Videocam
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Messages = ({ socket }) => {
  const { user, isAdmin, isEnseignant, isEtudiant } = usePermissions();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typing, setTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // États pour les modals
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  // ============ FONCTIONS API ============

  const loadConversations = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/messages/conversations', { headers });
      setConversations(res.data || []);
      setError('');
      return res.data;
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
      setError('Impossible de charger les conversations');
      throw err;
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      const headers = getAuthHeaders();
      const res = await axios.get(
        `/api/messages/conversations/${conversationId}/messages`,
        { headers }
      );
      setMessages(res.data || []);
      
      // Marquer comme lu
      await axios.put(
        `/api/messages/conversations/${conversationId}/read`,
        {},
        { headers }
      );
      loadUnreadCount();
    } catch (err) {
      console.error('Erreur chargement messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/messages/unread/count', { headers });
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      console.error('Erreur compteur non lus:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get('/api/users', { headers });
      setUsers(res.data || []);
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err);
    }
  };

  // ============ USEFFECTS CORRIGÉS ============

  // 1er useEffect - CHARGEMENT INITIAL (une seule fois)
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await loadConversations();
        await loadUnreadCount();
      } catch (err) {
        console.error('Erreur chargement initial:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // ← Tableau vide = une seule fois !

  // 2ème useEffect - SOCKET.IO (ne dépend que de socket)
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      // Ajouter le message à la conversation active
      if (data.conversation_id === selectedConversation?._id) {
        setMessages(prev => [...prev, data.message]);
      }
      // Mettre à jour la liste des conversations
      loadConversations();
      loadUnreadCount();
    };

    const handleTyping = (data) => {
      if (data.conversation_id === selectedConversation?._id) {
        setTypingUser(data.user);
        setTyping(true);
        setTimeout(() => setTyping(false), 3000);
      }
    };

    socket.on('new_message', handleNewMessage);
    socket.on('typing', handleTyping);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('typing', handleTyping);
    };
  }, [socket, selectedConversation?._id]);

  // 3ème useEffect - CHARGEMENT DES MESSAGES QUAND CONVERSATION CHANGE
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation._id);
    }
  }, [selectedConversation]);

  // 4ème useEffect - SCROLL VERS LE BAS
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ============ FONCTIONS MÉTIER ============

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        '/api/messages/messages',
        {
          conversation_id: selectedConversation._id,
          contenu: newMessage.trim()
        },
        { headers }
      );
      
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      // Mettre à jour la liste des conversations
      loadConversations();
    } catch (err) {
      console.error('Erreur envoi message:', err);
      setError('Erreur lors de l\'envoi du message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTyping = () => {
    if (socket && selectedConversation) {
      socket.emit('typing', {
        conversation_id: selectedConversation._id,
        user: {
          nom: user?.nom || 'Utilisateur',
          prenom: user?.prenom || ''
        }
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getConversationName = (conversation) => {
    if (!conversation) return 'Conversation';
    if (conversation.type === 'GROUPE' || conversation.type === 'CLASSE') {
      return conversation.nom || 'Groupe';
    }
    const otherParticipant = conversation.participants?.find(
      p => p.userId?._id?.toString() !== user?.id?.toString()
    );
    return otherParticipant ? `${otherParticipant.prenom || ''} ${otherParticipant.nom || ''}`.trim() || 'Utilisateur' : 'Utilisateur';
  };

  const getConversationAvatar = (conversation) => {
    if (!conversation) return '?';
    if (conversation.type === 'GROUPE' || conversation.type === 'CLASSE') {
      return conversation.nom?.charAt(0)?.toUpperCase() || 'G';
    }
    const otherParticipant = conversation.participants?.find(
      p => p.userId?._id?.toString() !== user?.id?.toString()
    );
    return otherParticipant?.prenom?.charAt(0)?.toUpperCase() || 'U';
  };

  const getConversationColor = (conversation) => {
    if (!conversation) return '#667eea';
    if (conversation.type === 'GROUPE') return '#8b5cf6';
    if (conversation.type === 'CLASSE') return '#3b82f6';
    return '#10b981';
  };

  const formatMessageDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const msgDate = new Date(date);
    if (msgDate.toDateString() === now.toDateString()) {
      return format(msgDate, 'HH:mm');
    }
    return format(msgDate, 'dd/MM/yyyy HH:mm');
  };

  const handleNewConversation = async () => {
    if (!selectedUsers.length) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        '/api/messages/conversations/prive',
        { destinataireId: selectedUsers[0] },
        { headers }
      );
      setNewConversationOpen(false);
      setSelectedUsers([]);
      await loadConversations();
      setSelectedConversation(res.data);
    } catch (err) {
      console.error('Erreur création conversation:', err);
      setError('Erreur lors de la création de la conversation');
    }
  };

  const handleNewGroup = async () => {
    if (selectedUsers.length < 2) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        '/api/messages/conversations/groupe',
        {
          nom: groupName || 'Nouveau groupe',
          participants: selectedUsers
        },
        { headers }
      );
      setNewGroupOpen(false);
      setSelectedUsers([]);
      setGroupName('');
      await loadConversations();
      setSelectedConversation(res.data);
    } catch (err) {
      console.error('Erreur création groupe:', err);
      setError('Erreur lors de la création du groupe');
    }
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(
        `/api/messages/messages/${selectedMessage._id}`,
        { headers }
      );
      setMessages(prev => prev.filter(m => m._id !== selectedMessage._id));
      setAnchorEl(null);
      setSelectedMessage(null);
    } catch (err) {
      console.error('Erreur suppression message:', err);
      setError('Erreur lors de la suppression du message');
    }
  };

  const openNewConversation = async () => {
    await loadUsers();
    setSelectedUsers([]);
    setNewConversationOpen(true);
  };

  const openNewGroup = async () => {
    await loadUsers();
    setSelectedUsers([]);
    setGroupName('');
    setNewGroupOpen(true);
  };

  const isMessageFromCurrentUser = (message) => {
    if (!message || !user) return false;
    return message.expediteur === user?.id || message.expediteur?._id === user?.id;
  };

  // Filtrage des conversations
  const filteredConversations = conversations.filter(conv => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;
    const name = getConversationName(conv).toLowerCase();
    return name.includes(search);
  });

  // ============ RENDU ============

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => { setError(''); loadConversations(); }}>
            Réessayer
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', p: 0 }}>
      {/* Sidebar - Liste des conversations */}
      <Paper
        sx={{
          width: 380,
          minWidth: 380,
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #e5e7eb',
          height: '100%'
        }}
      >
        {/* Header Sidebar */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              💬 Messages
              {unreadCount > 0 && (
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Box>
              <Tooltip title="Nouvelle conversation">
                <IconButton size="small" onClick={openNewConversation}>
                  <PersonAdd />
                </IconButton>
              </Tooltip>
              <Tooltip title="Nouveau groupe">
                <IconButton size="small" onClick={openNewGroup}>
                  <GroupAdd />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Liste des conversations */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List sx={{ p: 0 }}>
            {filteredConversations.length > 0 ? (
              filteredConversations.map((conv) => (
                <ListItem
                  key={conv._id}
                  button
                  selected={selectedConversation?._id === conv._id}
                  onClick={() => setSelectedConversation(conv)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid #f3f4f6',
                    bgcolor: selectedConversation?._id === conv._id ? '#f3f0ff' : 'transparent',
                    '&:hover': {
                      bgcolor: '#f9fafb'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="success"
                      variant="dot"
                      invisible={conv.type === 'GROUPE' || conv.type === 'CLASSE'}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: getConversationColor(conv),
                          color: 'white'
                        }}
                      >
                        {getConversationAvatar(conv)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={conv.unreadCount > 0 ? 'bold' : 'normal'}>
                          {getConversationName(conv)}
                        </Typography>
                        {conv.dernierMessage?.date && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(conv.dernierMessage.date), { addSuffix: true, locale: fr })}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          color={conv.unreadCount > 0 ? 'text.primary' : 'text.secondary'}
                          fontWeight={conv.unreadCount > 0 ? 'bold' : 'normal'}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px'
                          }}
                        >
                          {conv.dernierMessage?.contenu || 'Aucun message'}
                        </Typography>
                        {conv.unreadCount > 0 && (
                          <Chip
                            label={conv.unreadCount}
                            size="small"
                            sx={{
                              bgcolor: '#667eea',
                              color: 'white',
                              height: 20,
                              minWidth: 20,
                              fontSize: '0.6rem'
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {searchTerm ? 'Aucune conversation trouvée' : 'Aucune conversation'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<PersonAdd />}
                  onClick={openNewConversation}
                  sx={{ mt: 1 }}
                >
                  Démarrer une conversation
                </Button>
              </Box>
            )}
          </List>
        </Box>
      </Paper>

      {/* Zone de chat */}
      <Paper
        sx={{
          flex: 1,
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {selectedConversation ? (
          <>
            {/* Header chat */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: getConversationColor(selectedConversation) }}>
                  {getConversationAvatar(selectedConversation)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {getConversationName(selectedConversation)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedConversation.type === 'GROUPE' && `${selectedConversation.participants?.length || 0} membres`}
                    {selectedConversation.type === 'CLASSE' && 'Classe'}
                    {selectedConversation.type === 'PRIVE' && 'Conversation privée'}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Tooltip title="Appel audio">
                  <IconButton size="small">
                    <Phone />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Appel vidéo">
                  <IconButton size="small">
                    <Videocam />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                bgcolor: '#f9fafb'
              }}
            >
              {loadingMessages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {messages.length > 0 ? (
                    messages.map((msg) => {
                      const isMine = isMessageFromCurrentUser(msg);
                      return (
                        <Box
                          key={msg._id}
                          sx={{
                            display: 'flex',
                            justifyContent: isMine ? 'flex-end' : 'flex-start',
                            mb: 2
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              bgcolor: isMine ? '#667eea' : 'white',
                              color: isMine ? 'white' : '#1f2937',
                              borderRadius: 2,
                              p: 1.5,
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                          >
                            {!isMine && (
                              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                {msg.expediteur_prenom || ''} {msg.expediteur_nom || ''}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.contenu}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5
                              }}
                            >
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {formatMessageDate(msg.createdAt)}
                              </Typography>
                              {isMine && (
                                msg.lu ? (
                                  <DoneAll fontSize="small" sx={{ opacity: 0.7, fontSize: 14 }} />
                                ) : (
                                  <CheckCircle fontSize="small" sx={{ opacity: 0.5, fontSize: 14 }} />
                                )
                              )}
                              {isMine && (
                                <IconButton
                                  size="small"
                                  sx={{ opacity: 0.5, p: 0.5 }}
                                  onClick={(e) => {
                                    setSelectedMessage(msg);
                                    setAnchorEl(e.currentTarget);
                                  }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                      <Typography>Aucun message dans cette conversation</Typography>
                      <Typography variant="caption">Envoyez le premier message !</Typography>
                    </Box>
                  )}
                  {typing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {typingUser?.prenom || ''} {typingUser?.nom || 'Quelqu\'un'} écrit...
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, bgcolor: '#9ca3af', borderRadius: '50%', animation: 'typing 1.4s infinite' }} />
                        <Box sx={{ width: 6, height: 6, bgcolor: '#9ca3af', borderRadius: '50%', animation: 'typing 1.4s infinite 0.2s' }} />
                        <Box sx={{ width: 6, height: 6, bgcolor: '#9ca3af', borderRadius: '50%', animation: 'typing 1.4s infinite 0.4s' }} />
                      </Box>
                      <style>{`
                        @keyframes typing {
                          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                          30% { transform: translateY(-6px); opacity: 1; }
                        }
                      `}</style>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Input message */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid #e5e7eb',
                bgcolor: 'white'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                  <AttachFile />
                </IconButton>
                <IconButton size="small">
                  <InsertEmoticon />
                </IconButton>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Écrire un message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onKeyUp={handleTyping}
                  multiline
                  maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f3f4f6'
                    }
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    console.log('Fichier sélectionné:', e.target.files?.[0]);
                  }}
                />
                <Button
                  variant="contained"
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  sx={{
                    borderRadius: 3,
                    minWidth: 48,
                    bgcolor: '#667eea',
                    '&:hover': { bgcolor: '#5a67d8' }
                  }}
                >
                  <Send />
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#9ca3af'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              💬 Messages
            </Typography>
            <Typography variant="body2">
              Sélectionnez une conversation ou démarrez-en une nouvelle
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PersonAdd />}
                onClick={openNewConversation}
              >
                Nouvelle conversation
              </Button>
              <Button
                variant="outlined"
                startIcon={<GroupAdd />}
                onClick={openNewGroup}
              >
                Nouveau groupe
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Menu contextuel pour les messages */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedMessage(null);
        }}
      >
        <MuiMenuItem onClick={handleDeleteMessage}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => {
          if (selectedMessage?.contenu) {
            navigator.clipboard?.writeText(selectedMessage.contenu);
          }
          setAnchorEl(null);
          setSelectedMessage(null);
        }}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copier</ListItemText>
        </MuiMenuItem>
      </Menu>

      {/* Dialog Nouvelle conversation */}
      <Dialog open={newConversationOpen} onClose={() => setNewConversationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💬 Nouvelle conversation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sélectionner un utilisateur</InputLabel>
              <Select
                value={selectedUsers[0] || ''}
                onChange={(e) => setSelectedUsers([e.target.value])}
                label="Sélectionner un utilisateur"
              >
                {users
                  .filter(u => u._id !== user?.id)
                  .map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.nom || ''} {u.prenom || ''} ({u.role || 'Utilisateur'})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewConversationOpen(false);
            setSelectedUsers([]);
          }}>Annuler</Button>
          <Button
            onClick={handleNewConversation}
            variant="contained"
            disabled={!selectedUsers.length}
            sx={{ bgcolor: '#667eea' }}
          >
            Démarrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nouveau groupe */}
      <Dialog open={newGroupOpen} onClose={() => setNewGroupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>👥 Nouveau groupe</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nom du groupe"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Entrez un nom pour le groupe"
            />
            <FormControl fullWidth>
              <InputLabel>Sélectionner les membres</InputLabel>
              <Select
                multiple
                value={selectedUsers}
                onChange={(e) => setSelectedUsers(e.target.value)}
                label="Sélectionner les membres"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const u = users.find(user => user._id === id);
                      return u ? (
                        <Chip key={id} label={`${u.nom || ''} ${u.prenom || ''}`.trim() || u.email || 'Utilisateur'} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {users
                  .filter(u => u._id !== user?.id)
                  .map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.nom || ''} {u.prenom || ''} ({u.role || 'Utilisateur'})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Sélectionnez au moins 2 personnes
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewGroupOpen(false);
            setSelectedUsers([]);
            setGroupName('');
          }}>Annuler</Button>
          <Button
            onClick={handleNewGroup}
            variant="contained"
            disabled={selectedUsers.length < 2}
            sx={{ bgcolor: '#667eea' }}
          >
            Créer le groupe
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;


